import { createElement, Fragment, ReactNode } from 'react';
import { AttentionPanelItem } from '@/components/ui/AttentionPanel.tsx';
import { AttentionItemDto } from '@/types/stats/AttentionItemDto.ts';
import { getAttentionSeverityTone } from '@/types/stats/AttentionSeverity.utils.ts';
import { getAttentionTargetActionLabel } from '@/types/stats/AttentionTarget.utils.ts';
import { reportStatsContractBreach } from '@/types/stats/statsContract.ts';
import {
  attentionItemLink,
  AttentionLinkScope,
} from '@/features/dashboard/attention/routes/attentionLinks.ts';
import { AttentionKey } from '@/features/dashboard/attention/types/AttentionKey.ts';
import { attentionRefParts } from '@/features/dashboard/attention/types/AttentionRefDto.utils.ts';
import { CANCELLED_RECORD_CLASS } from '@/components/table/_fieldDefinitions/_base/cancelledRecord.ts';
import AttentionItemActions from '@/features/dashboard/attention/components/AttentionItemActions.tsx';
import { formatResolutionNote } from '@/features/dashboard/attention/types/AttentionResolutionDto.utils.ts';
import { getCurrencyLabel } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';
import { pluralForm, pluralize } from '@/utils/pluralize.ts';

const money = (item: AttentionItemDto): string | null =>
  item.amount ? formatPrice(item.amount.amount, item.amount.currency) : null;

const count = (item: AttentionItemDto): string => String(item.count);

const withRefs = (sentence: string, item: AttentionItemDto): ReactNode => {
  const { parts, rest } = attentionRefParts(item.refs);
  if (parts.length === 0) return sentence;

  const tail = rest > 0 ? ` i ${rest} więcej` : '';
  // Dopóki nic nie jest przekreślone, zdanie zostaje zwykłym napisem.
  if (!parts.some((part) => part.struck)) {
    return `${sentence} · ${parts.map((part) => part.label).join(', ')}${tail}`;
  }

  return createElement(
    Fragment,
    null,
    `${sentence} · `,
    ...parts.map((part, index) =>
      createElement(
        Fragment,
        { key: part.key },
        index > 0 && ', ',
        part.struck
          ? createElement(
              'span',
              { className: CANCELLED_RECORD_CLASS },
              part.label,
            )
          : part.label,
      ),
    ),
    tail,
  );
};

const bookings = (item: AttentionItemDto): ReactNode =>
  withRefs(
    pluralize(item.count, 'rezerwacja', 'rezerwacje', 'rezerwacji'),
    item,
  );

interface AttentionTemplate {
  label: string;
  detail: (item: AttentionItemDto) => ReactNode;
  badge: (item: AttentionItemDto) => string | null;
}

/**
 * Zdania panelu „Wymaga uwagi”. Backend przysyła klucz, liczbę, kwotę, dyskryminator
 * i odnośniki; polskie zdanie z liczebnikiem powstaje wyłącznie tutaj.
 */
const TEMPLATES: Record<AttentionKey, AttentionTemplate> = {
  [AttentionKey.OVERDUE_INSTALLMENTS]: {
    label: 'Raty po terminie',
    detail: (item) =>
      withRefs(pluralize(item.count, 'rata', 'raty', 'rat'), item),
    badge: money,
  },
  [AttentionKey.BUDGET_OVERRUN]: {
    label: 'Przekroczony budżet',
    detail: (item) =>
      item.discriminator
        ? `zaplanowane wydatki przekraczają plan w ${getCurrencyLabel(item.discriminator)}`
        : 'zaplanowane wydatki przekraczają plan',
    badge: money,
  },
  [AttentionKey.UNALLOCATED_TRANSACTIONS]: {
    label: 'Wpłaty do rozdysponowania',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'transakcja', 'transakcje', 'transakcji')} z nierozdysponowaną resztą`,
        item,
      ),
    badge: () => null,
  },
  [AttentionKey.OVERDUE_REFUNDS]: {
    label: 'Zwroty po terminie',
    detail: (item) =>
      withRefs(pluralize(item.count, 'zwrot', 'zwroty', 'zwrotów'), item),
    badge: money,
  },
  [AttentionKey.CANCELLED_BOOKING_HOLDING_MONEY]: {
    label: 'Anulowane rezerwacje z wpłatą bez zwrotu',
    detail: bookings,
    badge: money,
  },
  [AttentionKey.OVERPAYMENT]: {
    label: 'Nadpłaty do rozliczenia',
    detail: bookings,
    badge: money,
  },
  [AttentionKey.OVERDUE_EXPENSES]: {
    label: 'Faktury kosztowe po terminie',
    detail: (item) =>
      withRefs(pluralize(item.count, 'wydatek', 'wydatki', 'wydatków'), item),
    badge: money,
  },
  // Pigułka pokazuje kod waluty — sprawa nie ma kwoty.
  [AttentionKey.MISSING_BANK_ACCOUNT_FOR_CURRENCY]: {
    label: 'Brak konta bankowego dla waluty',
    detail: (item) => {
      const sentence = `${pluralize(item.count, 'rezerwacja', 'rezerwacje', 'rezerwacji')} z ratą przelewową`;
      return item.discriminator
        ? `${sentence} w ${getCurrencyLabel(item.discriminator)}`
        : sentence;
    },
    badge: (item) =>
      item.discriminator ? getCurrencyLabel(item.discriminator) : null,
  },
  [AttentionKey.TOUR_LIMIT_BELOW_OCCUPANCY]: {
    label: 'Limit wyjazdu poniżej obłożenia',
    detail: () => 'uczestników więcej niż limit',
    badge: () => null,
  },
  [AttentionKey.OPTION_LIMIT_BELOW_OCCUPANCY]: {
    label: 'Limit opcji poniżej obłożenia',
    detail: (item) =>
      `${pluralize(item.count, 'opcja lub wariant', 'opcje lub warianty', 'opcji lub wariantów')} ponad limitem`,
    badge: count,
  },
  [AttentionKey.MANDATORY_TRANSPORT_SOLD_OUT]: {
    label: 'Transport obowiązkowy wyprzedany',
    detail: () => 'wszystkie warianty pełne, zapisy otwarte',
    badge: () => null,
  },
  [AttentionKey.REQUIRED_OPTION_SOLD_OUT]: {
    label: 'Wymagana opcja wyprzedana',
    detail: (item) =>
      `${pluralize(item.count, 'opcja', 'opcje', 'opcji')} ${pluralForm(item.count, 'blokuje', 'blokują', 'blokuje')} zapisy`,
    badge: count,
  },
  [AttentionKey.UNSIGNED_REQUIRED_CONSENT]: {
    label: 'Niepodpisane wymagane dokumenty',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'dokument', 'dokumenty', 'dokumentów')} do podpisu`,
        item,
      ),
    badge: count,
  },
  [AttentionKey.PARTICIPANT_WITHOUT_REQUIRED_INSURANCE]: {
    label: 'Uczestnicy bez wymaganego ubezpieczenia',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'uczestnik', 'uczestników', 'uczestników')} bez wybranego ubezpieczenia`,
        item,
      ),
    badge: count,
  },
  [AttentionKey.MISSING_REQUIRED_FIELD]: {
    label: 'Brakujące dane uczestników',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'pole wymagane', 'pola wymagane', 'pól wymaganych')} do uzupełnienia`,
        item,
      ),
    badge: count,
  },
  [AttentionKey.WAITLIST_WITH_FREE_SEATS]: {
    label: 'Lista rezerwowa przy wolnych miejscach',
    detail: (item) =>
      `${pluralize(item.count, 'osoba', 'osoby', 'osób')} ${pluralForm(item.count, 'czeka', 'czekają', 'czeka')} na liście, a miejsca są wolne`,
    badge: count,
  },
  [AttentionKey.PENDING_CLIENT_CHANGE]: {
    label: 'Zmiany czekają na klienta',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'paczka zmian', 'paczki zmian', 'paczek zmian')} bez odpowiedzi klienta`,
        item,
      ),
    badge: count,
  },
  [AttentionKey.STALE_DRAFT_CHANGE]: {
    label: 'Zapomniane szkice zmian',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'szkic', 'szkice', 'szkiców')} ${pluralForm(item.count, 'starszy', 'starsze', 'starszych')} niż 14 dni`,
        item,
      ),
    badge: count,
  },
  [AttentionKey.CHANGE_REJECTED_BY_CLIENT]: {
    label: 'Klient odrzucił zmianę',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'paczka', 'paczki', 'paczek')} ${pluralForm(item.count, 'odrzucona', 'odrzucone', 'odrzuconych')} bez nowej propozycji`,
        item,
      ),
    badge: count,
  },
  [AttentionKey.MESSAGE_FAILED]: {
    label: 'Niewysłane wiadomości',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'wiadomość', 'wiadomości', 'wiadomości')} ${pluralForm(item.count, 'zakończona', 'zakończone', 'zakończonych')} błędem`,
        item,
      ),
    badge: count,
  },
  [AttentionKey.RECIPIENT_EMAIL_BOUNCING]: {
    label: 'Adresy e-mail odbijają',
    detail: (item) =>
      withRefs(
        `${pluralize(item.count, 'zapisujący', 'zapisujących', 'zapisujących')} nie dostaje wiadomości`,
        item,
      ),
    badge: count,
  },
};

/** Sprawy o miejscach — jedyne, które zakładka Opcji wyjmuje z pełnej listy. */
export const OPTIONS_TAB_KEYS: ReadonlySet<string> = new Set<string>([
  AttentionKey.TOUR_LIMIT_BELOW_OCCUPANCY,
  AttentionKey.OPTION_LIMIT_BELOW_OCCUPANCY,
  AttentionKey.MANDATORY_TRANSPORT_SOLD_OUT,
  AttentionKey.REQUIRED_OPTION_SOLD_OUT,
]);

/** Sprawy o wpłatach — panel widoku „Wpłaty” zakładki Finanse. */
export const FINANCES_TAB_KEYS: ReadonlySet<string> = new Set<string>([
  AttentionKey.OVERDUE_INSTALLMENTS,
  AttentionKey.OVERDUE_REFUNDS,
  AttentionKey.CANCELLED_BOOKING_HOLDING_MONEY,
  AttentionKey.OVERPAYMENT,
  AttentionKey.UNALLOCATED_TRANSACTIONS,
  AttentionKey.MISSING_BANK_ACCOUNT_FOR_CURRENCY,
]);

/** Sprawy o kosztach — panel widoku „Budżet” zakładki Finanse. */
export const BUDGET_TAB_KEYS: ReadonlySet<string> = new Set<string>([
  AttentionKey.BUDGET_OVERRUN,
  AttentionKey.OVERDUE_EXPENSES,
]);

/**
 * Identyfikator wiersza: klucz + dyskryminator + wyjazd (ta sama sprawa wraca raz na
 * walutę, a w zasięgu firmy raz na wyjazd). Odłożona pozycja to jedna sprawa, więc
 * jej id jest id sprawy — dwie odłożone tego samego klucza nie mogą się zlać.
 */
export const attentionItemId = (item: AttentionItemDto): string => {
  if (item.resolution && item.findingIds.length) {
    return `${item.key}-${item.findingIds[0]}`;
  }
  return `${item.key}-${item.discriminator ?? 'all'}-${item.tour?.id ?? 'scope'}`;
};

/** Nagłówek sprawy do dialogu i toastu — bez zdania z liczebnikiem. */
export const attentionItemLabel = (item: AttentionItemDto): string =>
  TEMPLATES[item.key as AttentionKey]?.label ?? item.key;

export interface AttentionPanelItemOptions {
  /** Zasięg panelu — wyznacza trasę odsyłacza. */
  scope: AttentionLinkScope;
  /** Gdy podane, wiersz dostaje menu z „Odłóż” (lista otwartych). */
  onResolve?: (item: AttentionItemDto) => void;
  /** Gdy podane, wiersz dostaje menu z „Przywróć” (lista odłożonych). */
  onReopen?: (item: AttentionItemDto) => void;
}

/** DTO spraw na wiersze panelu: zdanie z rejestru, ton z wagi, adres z mapy `attentionItemLink`. */
export const toAttentionPanelItems = (
  items: AttentionItemDto[],
  { scope, onResolve, onReopen }: AttentionPanelItemOptions,
): AttentionPanelItem[] =>
  items.map((item) => {
    const template = TEMPLATES[item.key as AttentionKey];

    if (!template) {
      reportStatsContractBreach(
        `Nieznana sprawa: "${item.key}". Dopisz jej szablon do AttentionKey.utils.ts.`,
      );
    }

    const link = attentionItemLink(item, scope);
    const actionLabel = getAttentionTargetActionLabel(item.target);
    const label = template ? template.label : item.key;

    return {
      id: attentionItemId(item),
      label,
      detail: template ? template.detail(item) : '',
      badge: template ? template.badge(item) : money(item),
      // Odłożona sprawa nie krzyczy — ton wagi zostaje na liście otwartych.
      tone: item.resolution
        ? 'neutral'
        : getAttentionSeverityTone(item.severity),
      note: item.resolution ? formatResolutionNote(item.resolution) : undefined,
      link,
      actionLabel,
      // `createElement` zamiast JSX: rejestr jest plikiem `.ts`, a menu wiersza musi znać
      // pozycję i cel, które właśnie tu powstają.
      actions:
        onResolve || onReopen
          ? createElement(AttentionItemActions, {
              item,
              label,
              link,
              actionLabel,
              onResolve,
              onReopen,
            })
          : undefined,
    };
  });
